import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

import { fetchProcessingFactoryDetail } from '#/api/momMgmt/settings/processingFactory/fetchDetail';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { processingFactoryDetailQueryKey } from '#/utils/queryKeys';

/**
 * 加工廠／工作倉／目標倉；未選加工廠時工作倉唯讀；換廠清空工作倉（初次 hydrate 除外）。
 */
const WorkOrderWarehouseFields = ({
  control,
  errors,
  setValue,
  clearErrors,
  factoryOptions = [],
  warehouseOptions = [],
  isFactoryLoading = false,
  isWarehouseLoading = false,
  disabled = false,
  skipClearOnFactoryChangeRef,
}) => {
  const processingFactoryId = useWatch({ control, name: 'processingFactoryId' });
  const prevFactoryIdRef = useRef(undefined);

  const factoryDetailQuery = useQuery({
    queryKey: processingFactoryDetailQueryKey(processingFactoryId),
    queryFn: () => fetchProcessingFactoryDetail(processingFactoryId),
    enabled: Boolean(processingFactoryId),
  });

  const allowedWarehouseIds = useMemo(() => {
    const ids = factoryDetailQuery.data?.data?.warehouseIds ?? [];
    return new Set(ids);
  }, [factoryDetailQuery.data?.data?.warehouseIds]);

  const workWarehouseOptions = useMemo(() => {
    if (!processingFactoryId) {
      return [];
    }
    if (factoryDetailQuery.isLoading) {
      return [];
    }
    return warehouseOptions.filter((option) => allowedWarehouseIds.has(option.value));
  }, [
    processingFactoryId,
    warehouseOptions,
    allowedWarehouseIds,
    factoryDetailQuery.isLoading,
  ]);

  useEffect(() => {
    const prev = prevFactoryIdRef.current;
    prevFactoryIdRef.current = processingFactoryId;

    if (prev === undefined) {
      return;
    }
    if (prev === processingFactoryId) {
      return;
    }
    if (skipClearOnFactoryChangeRef?.current) {
      skipClearOnFactoryChangeRef.current = false;
      return;
    }
    setValue('workWarehouseId', '', { shouldDirty: true, shouldValidate: false });
    clearErrors?.('workWarehouseId');
  }, [processingFactoryId, setValue, clearErrors, skipClearOnFactoryChangeRef]);

  return (
    <>
      <FloatingSelect
        id="processingFactoryId"
        label="加工廠"
        options={factoryOptions}
        control={control}
        errors={errors}
        isClearable
        isDisabled={disabled || isFactoryLoading}
        noOptionsMessage="無可用加工廠"
      />
      <FloatingSelect
        id="workWarehouseId"
        label="工作倉"
        options={workWarehouseOptions}
        control={control}
        errors={errors}
        isDisabled={
          disabled
          || isWarehouseLoading
          || !processingFactoryId
          || factoryDetailQuery.isLoading
        }
        noOptionsMessage={
          processingFactoryId
            ? '此加工廠無可用工作倉'
            : '請先選擇加工廠'
        }
      />
      <FloatingSelect
        id="targetWarehouseId"
        label="目標倉"
        options={warehouseOptions}
        control={control}
        errors={errors}
        isDisabled={disabled || isWarehouseLoading}
        noOptionsMessage="無可用倉別"
      />
    </>
  );
};

WorkOrderWarehouseFields.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  clearErrors: PropTypes.func,
  factoryOptions: PropTypes.arrayOf(PropTypes.object),
  warehouseOptions: PropTypes.arrayOf(PropTypes.object),
  isFactoryLoading: PropTypes.bool,
  isWarehouseLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  skipClearOnFactoryChangeRef: PropTypes.shape({ current: PropTypes.bool }),
};

export default WorkOrderWarehouseFields;
